<script setup>
// @ts-check
/**
 * @import { Ref, PropType } from 'vue'
 * @import { ComponentProps } from '../../propTypes'
 */
import { onMounted, ref } from 'vue'
import { setup } from './useLoginForm'
import { route } from '@/utils/ziggy.js'

defineProps({
  errorList: {
    /** @type {PropType<ComponentProps["errorList"]>} */
    type: Object,
    required: true,
  },
})
/** @type { Ref<HTMLFormElement|undefined>} */
const refForm = ref()

const actionUrl = /** @type {HTMLInputElement|undefined} */ (document.getElementById('url_login_action'))?.value
const passwordResetUrl = /** @type {HTMLInputElement|undefined} */ (document.getElementById('url_password_reset'))
  ?.value
const csrfToken = /** @type {HTMLInputElement|undefined} */ (document.getElementById('csrf_token'))?.value
const pp = /** @type {HTMLInputElement|undefined} */ (document.getElementById('pp'))?.value

const channel = /** @type {HTMLInputElement|undefined} */ (document.getElementById('channel'))?.value
const comparelpcd = /** @type {HTMLInputElement|undefined} */ (document.getElementById('comparelpcd'))?.value
const routeLp = /** @type {HTMLInputElement|undefined} */ (document.getElementById('routeLp'))?.value
const pk = new URLSearchParams(window.location.search).get('PK')

const {
  loginId,
  loginPw,
  errorMessageList,
  btnDisabled,
  isResetDone,
  formSubmit,
  checkLoginPW,
  checkLoginID,
  grecaptchaToken,
} = setup(refForm)

onMounted(() => {
  const grecaptchaSiteKey = /** @type {HTMLInputElement|undefined} */ (document.getElementById('grecaptcha_site_key'))
    ?.value
  if (grecaptchaSiteKey && grecaptchaSiteKey.length) {
    const src = 'https://www.google.com/recaptcha/enterprise.js?render=' + grecaptchaSiteKey
    const recaptchaScript = document.createElement('script')
    recaptchaScript.setAttribute('src', src)
    document.head.appendChild(recaptchaScript)
  }
})
</script>

<template>
  <form
    method="post"
    :action="actionUrl"
    class="loginForm"
    accept-charset="utf-8"
    ref="refForm"
  >
    <input
      type="hidden"
      name="grecaptcha_token"
      id="grecaptcha_token"
      v-model="grecaptchaToken"
    />
    <input
      type="hidden"
      name="_token"
      :value="csrfToken"
    />
    <input
      type="hidden"
      name="_pp_"
      :value="pp"
    />
    <input
      type="hidden"
      name="channel"
      :value="channel"
    />
    <input
      type="hidden"
      name="comparelpcd"
      :value="comparelpcd"
    />
    <input
      type="hidden"
      name="routeLp"
      :value="routeLp"
    />
    <div class="formArea">
      <div
        v-if="isResetDone == '1'"
        class="md_informationCopy js_resetDone"
        style=""
      >
        <div class="catchSet">
          <div class="catch">パスワードを再設定しました</div>
        </div>
        <div class="copySet">
          <p class="copy">新しいパスワードでログインをお願いいたします。</p>
        </div>
      </div>
      <div
        v-if="Object.keys(errorList).length > 0"
        class="md_errorCopy"
      >
        <template
          v-for="(erros, key) in errorList"
          :key="key"
        >
          <template
            v-for="(error, i) in erros"
            :key="i"
          >
            <p class="errorCatch">
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ error }}
            </p>
          </template>
        </template>
      </div>
      <div class="formSet">
        <div class="title">メールアドレス</div>
        <div class="form">
          <input
            id="address"
            type="email"
            style="ime-mode: disabled"
            placeholder="メールアドレスを入力"
            class="form--line form--w100"
            name="login_id"
            v-model="loginId"
            v-on:blur="checkLoginID"
            v-bind:class="{
              md_error: errorMessageList.login_id.length > 0 || errorList.account_notfound || errorList.login_id,
            }"
          />
          <template v-if="errorMessageList.login_id.length > 0">
            <p
              v-for="(message, index) in errorMessageList.login_id"
              :key="index"
              class="errorMessage"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ message }}
            </p>
          </template>
        </div>
      </div>

      <div class="formSet">
        <div class="title">パスワード</div>
        <div class="form">
          <input
            id="password"
            style="ime-mode: disabled"
            placeholder="パスワードを入力"
            class="form--line form--w100"
            name="login_password"
            v-model="loginPw"
            type="password"
            v-on:blur="checkLoginPW"
            v-bind:class="{
              md_error: errorMessageList.login_pw.length > 0 || errorList.account_notfound || errorList.login_password,
            }"
          />
          <p
            v-for="(message, index) in errorMessageList.login_pw"
            :key="index"
            class="errorMessage"
          >
            <span class="iconImgPart">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 150 1100 1100"
              >
                <path
                  d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                  fill="#f04438"
                ></path>
              </svg> </span
            >{{ message }}
          </p>
        </div>
      </div>
    </div>
    <div class="btnArea">
      <div class="btnSet">
        <a
          href="javascript:void(0);"
          @click="formSubmit"
          class="md_btn md_btn--submit"
          :class="{ 'md_btn--disable': btnDisabled }"
          >ログイン</a
        >
      </div>

      <div class="md_informationCopy md_informationCopy--yellow">
        <div class="catchSet">
          <div class="catch">
            Apple・Facebook・LINEアカウントを利用したログイン機能は終了しました。今後は、登録済みのメールアドレスとパスワードでログインしてください。
          </div>
        </div>
      </div>

      <div class="linkSet">
        <a
          :href="passwordResetUrl"
          class="md_textLink"
          >パスワードを忘れた方</a
        >
        <a
          :href="route('student.login.help', { PK: pk })"
          class="md_textLink"
          >ログインでお困りの方</a
        >
      </div>
    </div>
  </form>
</template>
